import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listMaps,
  createMap,
  deleteMap,
} from '@/persistence/hybridPersistence';
import { usePlanGating } from '@/hooks/usePlanGating';
import { useIsMobile } from '@/hooks/useMediaQuery';
import type { MapListEntry } from '@/persistence/mapList';

interface MapListSidebarProps {
  currentMapId: string | null;
  onOpenMap: (id: string) => void;
  onNewMap: (id: string) => void | Promise<void>;
  onOpenPricing?: () => void;
}

const SIDEBAR_WIDTH = 260;

export function MapListSidebar({ currentMapId, onOpenMap, onNewMap, onOpenPricing }: MapListSidebarProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [maps, setMaps] = useState<MapListEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { canCreateMap, limits, isFreePlan } = usePlanGating();

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listMaps();
      // Sort by most recently updated first
      list.sort((a, b) => b.updatedAt - a.updatedAt);
      setMaps(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void refreshList();
    }
  }, [open, refreshList]);

  const handleNew = async () => {
    if (!canCreateMap(maps.length)) {
      setShowUpgrade(true);
      return;
    }
    setShowUpgrade(false);
    try {
      const id = await createMap(t('common.untitledMap'));
      await refreshList();
      await onNewMap(id);
    } catch (err) {
      console.error('Failed to create map:', err);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteMap(id);
    await refreshList();
    setShowUpgrade(false);
  };

  const formatDate = (timestamp: number): string => {
    const d = new Date(timestamp);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const mapCountLabel = isFreePlan
    ? t('sidebar.mapCountWithLimit', { count: maps.length, limit: limits.maxMaps })
    : t('sidebar.mapCount', { count: maps.length });

  const toggleButtonStyle: React.CSSProperties = isMobile
    ? {
        position: 'absolute',
        top: 52,
        left: 8,
        zIndex: 20,
        background: 'var(--bg-surface)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-default)',
        borderRadius: 6,
        padding: '6px 10px',
        cursor: 'pointer',
        fontSize: 'var(--font-size-base)',
        fontFamily: 'var(--font-family)',
      }
    : {
        position: 'absolute',
        top: 52,
        left: open ? SIDEBAR_WIDTH + 8 : 8,
        zIndex: 20,
        background: 'var(--bg-surface)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-default)',
        borderRadius: 6,
        padding: '6px 10px',
        cursor: 'pointer',
        fontSize: 'var(--font-size-base)',
        fontFamily: 'var(--font-family)',
        transition: 'left 0.2s ease',
      };

  const containerStyle: React.CSSProperties = isMobile
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: '100%',
        maxWidth: 320,
        zIndex: 35,
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.25s ease',
        background: 'var(--bg-app)',
        borderRight: '1px solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--font-family)',
      }
    : {
        position: 'absolute',
        top: 0,
        left: open ? 0 : -SIDEBAR_WIDTH,
        width: SIDEBAR_WIDTH,
        height: '100%',
        background: 'var(--bg-app)',
        borderRight: '1px solid var(--border-subtle)',
        zIndex: 15,
        display: 'flex',
        flexDirection: 'column',
        transition: 'left 0.2s ease',
        fontFamily: 'var(--font-family)',
      };

  return (
    <>
      {/* Backdrop overlay for mobile */}
      {isMobile && open && (
        <div
          onClick={() => {
            setOpen(false);
            setShowUpgrade(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 34,
            background: 'var(--bg-overlay)',
          }}
        />
      )}

      {/* Toggle button */}
      <button
        onClick={() => {
          setOpen((v) => !v);
          setShowUpgrade(false);
        }}
        title={open ? t('sidebar.closeSidebar') : t('sidebar.openSavedMaps')}
        style={toggleButtonStyle}
      >
        {!isMobile && open ? '<' : t('sidebar.maps')}
      </button>

      {/* Sidebar panel */}
      <div style={containerStyle}>
        {/* Header */}
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <span style={{ color: 'var(--text-primary)', fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
              {t('sidebar.savedMaps')}
            </span>
            {!loading && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--font-size-xs)', marginTop: 2 }}>
                {mapCountLabel}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => void handleNew()}
              style={{
                background: 'var(--color-primary)',
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                padding: '4px 10px',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {t('sidebar.new')}
            </button>
            {isMobile && (
              <button
                onClick={() => {
                  setOpen(false);
                  setShowUpgrade(false);
                }}
                title={t('sidebar.closeSidebar')}
                style={{
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                  padding: '2px 6px',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                X
              </button>
            )}
          </div>
        </div>

        {/* Upgrade prompt */}
        {showUpgrade && (
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--border-subtle)',
              background: '#1e1b4b',
            }}
          >
            <p style={{
              margin: '0 0 8px',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-primary)',
            }}>
              {t('sidebar.freePlanLimit', { limit: limits.maxMaps })}
            </p>
            <button
              onClick={onOpenPricing}
              style={{
                display: 'inline-block',
                padding: '6px 14px',
                background: 'var(--color-secondary)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              {t('common.upgrade')}
            </button>
          </div>
        )}

        {/* Map list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {loading && (
            <div style={{ color: 'var(--text-tertiary)', fontSize: 12, padding: '8px 16px' }}>
              {t('sidebar.loading')}
            </div>
          )}
          {!loading && maps.length === 0 && (
            <div style={{ color: 'var(--text-tertiary)', fontSize: 12, padding: '8px 16px' }}>
              {t('sidebar.noMaps')}
            </div>
          )}
          {maps.map((m) => (
            <div
              key={m.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                gap: 8,
                background: m.id === currentMapId ? 'var(--bg-surface)' : 'transparent',
                cursor: 'pointer',
              }}
              onClick={() => onOpenMap(m.id)}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      color: 'var(--text-primary)',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {m.title}
                  </span>
                  {m.ownerEmail && (
                    <span
                      style={{
                        padding: '1px 5px',
                        background: 'var(--bg-active)',
                        color: 'var(--text-link)',
                        borderRadius: 3,
                        fontSize: 9,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {t('sidebar.shared')}
                    </span>
                  )}
                </div>
                {m.ownerEmail ? (
                  <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--font-size-xs)', marginTop: 2 }}>
                    {t('sidebar.sharedBy', { email: m.ownerEmail })}
                  </div>
                ) : (
                  <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--font-size-xs)', marginTop: 2 }}>
                    {formatDate(m.updatedAt)}
                  </div>
                )}
              </div>
              {(!m.role || m.role === 'owner') && !m.ownerEmail && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleDelete(m.id);
                  }}
                  title={t('sidebar.deleteMap')}
                  style={{
                    background: 'transparent',
                    color: 'var(--text-tertiary)',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-base)',
                    padding: '2px 6px',
                    borderRadius: 'var(--radius-sm)',
                    flexShrink: 0,
                  }}
                >
                  x
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
